import { useState } from "react";
import { ChevronDown, ChevronRight, Users, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export interface ClinicianData {
  npi: string;
  name: string;
  specialty?: string;
  mipsEligible: boolean;
  score: number | null;
  medicareRevenue: number;
  adjustmentPercent?: number;
  dollarImpact?: number;
  zone?: string;
  reason?: string; // Reason for ineligibility
}

interface ClinicianBreakdownTableProps {
  eligibleClinicians: ClinicianData[];
  ineligibleClinicians: ClinicianData[];
  className?: string;
}

function formatCurrency(amount: number): string {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absAmount);
  
  if (amount < 0) return '-' + formatted;
  if (amount > 0) return '+' + formatted;
  return formatted;
}

function formatRevenueShort(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount.toFixed(0)}`;
}

export function ClinicianBreakdownTable({
  eligibleClinicians,
  ineligibleClinicians,
  className
}: ClinicianBreakdownTableProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  const totalClinicians = eligibleClinicians.length + ineligibleClinicians.length;
  const allClinicians = [...eligibleClinicians, ...ineligibleClinicians];

  // Calculate score distribution for summary
  const scoreDistribution = {
    excellent: eligibleClinicians.filter(c => c.score !== null && c.score >= 90).length,
    good: eligibleClinicians.filter(c => c.score !== null && c.score >= 75 && c.score < 90).length,
    penalty: eligibleClinicians.filter(c => c.score !== null && c.score < 75).length,
    noScore: eligibleClinicians.filter(c => c.score === null).length,
  };

  if (totalClinicians === 0) {
    return (
      <div className={cn("w-full", className)}>
        <div className="rounded-lg border border-dashed p-6 text-center">
          <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No clinician data available. Upload clinician eligibility file in Configuration → QPP Reports.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="ghost" 
            className="w-full justify-between p-4 h-auto hover:bg-muted/50 rounded-lg border"
          >
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-muted-foreground" />
              <div className="text-left">
                <p className="font-medium">
                  Clinician Breakdown ({eligibleClinicians.length} MIPS Eligible
                  {ineligibleClinicians.length > 0 && `, ${ineligibleClinicians.length} Non-Eligible`})
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {scoreDistribution.excellent > 0 && (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300">
                      {scoreDistribution.excellent} @ 90+
                    </Badge>
                  )}
                  {scoreDistribution.good > 0 && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300">
                      {scoreDistribution.good} @ 75-89
                    </Badge>
                  )}
                  {scoreDistribution.penalty > 0 && (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300">
                      {scoreDistribution.penalty} in Penalty
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            {isOpen ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )}
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-2">
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Clinician</TableHead>
                  <TableHead>NPI</TableHead>
                  <TableHead className="text-center">MIPS Eligible</TableHead>
                  <TableHead className="text-center">Score</TableHead>
                  <TableHead className="text-right">Medicare Revenue</TableHead>
                  <TableHead className="text-right">Adjustment %</TableHead>
                  <TableHead className="text-right">Impact</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Eligible Clinicians First */}
                {eligibleClinicians.map((clinician) => (
                  <TableRow key={clinician.npi}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{clinician.name}</p>
                        {clinician.specialty && (
                          <p className="text-xs text-muted-foreground">{clinician.specialty}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{clinician.npi}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        ✓
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {clinician.score !== null ? (
                        <Badge 
                          variant="outline" 
                          className={cn(
                            clinician.score >= 90 && "bg-emerald-50 text-emerald-700 border-emerald-200",
                            clinician.score >= 75 && clinician.score < 90 && "bg-blue-50 text-blue-700 border-blue-200",
                            clinician.score < 75 && "bg-red-50 text-red-700 border-red-200"
                          )}
                        >
                          {Math.round(clinician.score)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatRevenueShort(clinician.medicareRevenue)}
                    </TableCell>
                    <TableCell className="text-right">
                      {clinician.adjustmentPercent !== undefined ? (
                        <span className={cn(
                          clinician.adjustmentPercent < 0 && "text-red-600",
                          clinician.adjustmentPercent > 0 && "text-emerald-600"
                        )}>
                          {clinician.adjustmentPercent > 0 ? '+' : ''}{(clinician.adjustmentPercent * 100).toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {clinician.dollarImpact !== undefined ? (
                        <span className={cn(
                          clinician.dollarImpact < 0 && "text-red-600",
                          clinician.dollarImpact > 0 && "text-emerald-600"
                        )}>
                          {formatCurrency(clinician.dollarImpact)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Ineligible Clinicians (grayed out) */}
                {ineligibleClinicians.map((clinician) => (
                  <TableRow key={clinician.npi} className="bg-muted/30">
                    <TableCell>
                      <div className="text-muted-foreground">
                        <p className="font-medium">{clinician.name}</p>
                        {clinician.specialty && (
                          <p className="text-xs">{clinician.specialty}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{clinician.npi}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="text-muted-foreground border-muted">
                        —
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">—</TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatRevenueShort(clinician.medicareRevenue)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">—</TableCell>
                    <TableCell className="text-right">
                      <span className="text-xs text-muted-foreground">
                        {clinician.reason || 'Not MIPS eligible'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

